import { ArrowRight, Check, Code2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CodeBlock } from '../components/ui';
import { cn } from '../lib/format';
import { useSession } from '../lib/session';
import { useMarketingCopy, type HubId, type MarketingPageId } from './content';
import { CODE, HUB_PAGES, HUB_PATHS, hubOf, PAGE_PATHS, RELATED, VISUALS } from './content/shared';
import { Breadcrumbs, MarketingShell } from './MarketingLayout';
import { useLocalePath, usePageLocale } from './PublicRoute';
import { Visual } from './Visual';

function CtaButtons({ dark }: { dark?: boolean }) {
  const locale = usePageLocale();
  const { ui } = useMarketingCopy(locale);
  const user = useSession((s) => s.user);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Link
        to={user ? '/app/templates' : '/signup'}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold shadow-lg',
          dark
            ? 'bg-white text-indigo-950 hover:bg-indigo-50'
            : 'bg-brand-600 text-white shadow-brand-500/25 hover:bg-brand-700',
        )}
      >
        {user ? ui.openApp : ui.startFree} <ArrowRight className="size-4" />
      </Link>
      <a
        href="/docs"
        className={cn(
          'inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold',
          dark
            ? 'border border-white/20 text-white hover:bg-white/10'
            : 'text-zinc-700 ring-1 ring-zinc-200 hover:bg-zinc-50',
        )}
      >
        <Code2 className="size-4" /> {ui.seeApi}
      </a>
    </div>
  );
}

/** "✓ yes" / "✗ no" cells of comparison tables start with a mark; the rest is plain text. */
function Cell({ value }: { value: string }) {
  const mark = value[0];
  if (mark === '✓' || mark === '✗') {
    const ok = mark === '✓';
    return (
      <span className="flex gap-2">
        {ok ? (
          <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        ) : (
          <X className="mt-0.5 size-4 shrink-0 text-zinc-400" />
        )}
        <span>{value.slice(1).trim()}</span>
      </span>
    );
  }
  return <>{value}</>;
}

/** A use case or comparison page: everything comes from content/<lang>.ts and shared.ts. */
export function MarketingPage({ id }: { id: MarketingPageId }) {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const { ui } = copy;
  const page = copy.pages[id];
  const hub = hubOf(id);
  const lp = useLocalePath();
  const code = CODE[id];

  return (
    <MarketingShell>
      {/* First screen */}
      <section className="relative overflow-hidden bg-[#0f0d2e] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_80%_10%,rgba(99,102,241,0.45),transparent),radial-gradient(40%_40%_at_0%_100%,rgba(236,72,153,0.22),transparent)]" />
        <div className="relative mx-auto max-w-5xl px-6 pt-8 pb-20">
          <div className="[&_a]:text-indigo-200 [&_a:hover]:text-white [&_span]:text-indigo-100">
            <Breadcrumbs
              items={[
                { label: ui.home, to: lp('/') },
                { label: copy.hubs[hub].label, to: lp(HUB_PATHS[hub]) },
                { label: page.label },
              ]}
            />
          </div>
          <div className="mt-10 inline-flex rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-indigo-100">
            {page.eyebrow}
          </div>
          <h1 className="mt-5 max-w-4xl text-4xl leading-[1.1] font-extrabold tracking-tight md:text-5xl">
            {page.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-indigo-100/80">{page.lead}</p>
          <div className="mt-8">
            <CtaButtons dark />
          </div>
          <p className="mt-4 text-sm text-indigo-200/70">{ui.noCard}</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-6">
        <div className="relative -mt-10">
          <Visual
            visual={VISUALS[id]}
            samples={page.samples}
            locale={locale}
            caption={VISUALS[id].kind === 'sizes' ? ui.sizesCaption : ui.rowsCaption}
          />
        </div>

        {page.pains && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
              {page.pains.title}
            </h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {page.pains.items.map((item) => (
                <li
                  key={item}
                  className="flex gap-3 rounded-xl bg-rose-50/60 p-4 text-zinc-700 ring-1 ring-rose-100"
                >
                  <X className="mt-0.5 size-5 shrink-0 text-rose-500" aria-label={ui.painsLabel} />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {page.table && (
          <section className="mt-20">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
              {page.table.title}
            </h2>
            <div className="mt-8 overflow-x-auto rounded-2xl ring-1 ring-zinc-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-zinc-50 text-zinc-900">
                  <tr>
                    {page.table.head.map((h, i) => (
                      <th
                        key={h}
                        scope="col"
                        className={cn('px-5 py-3 font-semibold', i === 1 && 'text-brand-700')}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                  {page.table.rows.map(([criterion, ours, theirs]) => (
                    <tr key={criterion} className="align-top">
                      <th scope="row" className="px-5 py-3 font-medium text-zinc-900">
                        {criterion}
                      </th>
                      <td className="bg-indigo-50/40 px-5 py-3">
                        <Cell value={ours} />
                      </td>
                      <td className="px-5 py-3">
                        <Cell value={theirs} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="mt-20">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
            {page.steps.title}
          </h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-3">
            {page.steps.items.map((step, i) => (
              <li
                key={step.title}
                className="relative rounded-2xl bg-white p-6 ring-1 ring-zinc-200"
              >
                <div className="absolute -top-4 left-6 flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm font-bold text-white shadow-md">
                  {i + 1}
                </div>
                <h3 className="mt-2 text-lg font-semibold text-zinc-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {page.code && code && (
          <section className="mt-20 grid items-start gap-8 lg:grid-cols-[2fr_3fr]">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
                {page.code.title}
              </h2>
              <p className="mt-4 leading-relaxed text-zinc-600">{page.code.text}</p>
            </div>
            <CodeBlock language={code.language} code={code.code} />
          </section>
        )}

        <section className="mt-20">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
            {page.features.title}
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {page.features.items.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl bg-gradient-to-br from-indigo-50/70 to-white p-6 ring-1 ring-zinc-200"
              >
                <h3 className="flex items-start gap-2 font-semibold text-zinc-900">
                  <Check className="mt-0.5 size-5 shrink-0 text-emerald-600" />
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
            {ui.faqTitle}
          </h2>
          <div className="mt-6 divide-y divide-zinc-200">
            {page.faq.map(({ q, a }) => (
              <div key={q} className="py-5">
                <h3 className="font-semibold text-zinc-900">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20">
          <h2 className="text-xl font-bold tracking-tight text-zinc-900">{ui.relatedTitle}</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {RELATED[id].map((rid) => (
              <Link
                key={rid}
                to={lp(PAGE_PATHS[rid])}
                className="group rounded-2xl p-5 ring-1 ring-zinc-200 transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-indigo-200"
              >
                <div className="font-semibold text-zinc-900 group-hover:text-brand-700">
                  {copy.pages[rid].label}
                </div>
                <p className="mt-1 text-sm text-zinc-600">{copy.pages[rid].summary}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="px-6 py-24">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 text-white shadow-xl sm:p-12">
          <h2 className="text-3xl font-extrabold tracking-tight">{page.cta.title}</h2>
          <p className="mt-3 max-w-2xl text-indigo-50">{page.cta.text}</p>
          <div className="mt-8">
            <CtaButtons dark />
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

/** List of the use cases or of the comparisons. */
export function HubPage({ hub }: { hub: HubId }) {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const lp = useLocalePath();
  const h = copy.hubs[hub];
  return (
    <MarketingShell>
      <section className="mx-auto max-w-5xl px-6 pt-8 pb-24">
        <Breadcrumbs items={[{ label: copy.ui.home, to: lp('/') }, { label: h.label }]} />
        <div className="mt-10 inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
          {h.eyebrow}
        </div>
        <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
          {h.h1}
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-zinc-600">{h.lead}</p>
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {HUB_PAGES[hub].map((id) => (
            <Link
              key={id}
              to={lp(PAGE_PATHS[id])}
              className="group rounded-2xl bg-gradient-to-br from-indigo-50/60 to-white p-6 ring-1 ring-zinc-200 transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-indigo-200"
            >
              <h2 className="text-lg font-semibold text-zinc-900 group-hover:text-brand-700">
                {copy.pages[id].label}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{copy.pages[id].summary}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-600">
                {copy.ui.learnMore} <ArrowRight className="size-4" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-16">
          <CtaButtons />
        </div>
      </section>
    </MarketingShell>
  );
}
